import React, { ReactNode, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Save, Loader2 } from 'lucide-react';
import { Button } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import { GuideStepType } from '@/services/apis/guides';
import { useAutoSave } from '@/hooks/useAutoSave';

const gradientMap: Record<GuideStepType, string> = {
  [GuideStepType.RICH_TEXT]: 'from-[#667eea] to-[#764ba2]',
  [GuideStepType.IMAGE]: 'from-green-500 to-green-600',
  [GuideStepType.VIDEO]: 'from-red-500 to-red-600',
  [GuideStepType.PDF]: 'from-orange-500 to-orange-600',
  [GuideStepType.QUESTION]: 'from-purple-500 to-purple-600',
  [GuideStepType.CODE]: 'from-gray-500 to-gray-600',
};

interface StepEditorWrapperProps {
  title: string;
  stepType: GuideStepType;
  onSave: () => Promise<void> | void;
  isSaveDisabled: boolean;
  isPending: boolean;
  shouldAutoSave?: boolean;
  onAutoSaveComplete?: () => void;
  children: ReactNode;
}

const StepEditorWrapper: React.FC<StepEditorWrapperProps> = ({
  title,
  stepType,
  onSave,
  isSaveDisabled,
  isPending,
  shouldAutoSave = false,
  onAutoSaveComplete,
  children,
}) => {
  const { t } = useTranslation('admin');
  const gradientClass = gradientMap[stepType] || 'from-gray-500 to-gray-600';

  // Wrap onSave to ensure it returns a Promise
  const handleAutoSave = useCallback(async () => {
    await onSave();
  }, [onSave]);

  // Handle auto-save complete - noop if not provided
  const handleAutoSaveComplete = useCallback(() => {
    onAutoSaveComplete?.();
  }, [onAutoSaveComplete]);

  const { isAutoSaving } = useAutoSave(
    shouldAutoSave,
    handleAutoSave,
    handleAutoSaveComplete,
    [GuideStepType.IMAGE, GuideStepType.VIDEO, GuideStepType.PDF].includes(stepType) ? 1000 : 5000,
  );

  return (
    <motion.div
      className="bg-background rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Header */}
      <div className={`bg-gradient-to-r ${gradientClass} px-6 py-4 text-white`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h3 className="font-semibold">{title}</h3>
            {isAutoSaving && (
              <div className="flex items-center gap-1.5 text-white/80 text-sm">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span>{t('common.autoSaving') || 'Auto-saving...'}</span>
              </div>
            )}
          </div>
          <Button
            color="primary"
            variant="solid"
            onPress={onSave}
            isDisabled={isSaveDisabled || isAutoSaving}
            isLoading={isPending && !isAutoSaving}
            startContent={!isPending && <Save className="w-4 h-4" />}
            className="bg-white/20 hover:bg-white/30"
          >
            {t('common.save')}
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">{children}</div>
    </motion.div>
  );
};

export default StepEditorWrapper;
